import React, { Fragment, useState } from "react";
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { useRecordContext } from "react-admin";

// Backend contract (IT-3326): LCA calculation logs store `warnings` as JSON
// ContextWarning[] = { type, severity, label, stage, stages, message, affected_count, affected[] }
// grouped by root cause (reportData.service.ts getContextWarnings). Legacy per-entity logs are
// converted by the backend migration 20261006120000; any other payload is left to the JSON view.
export const SEVERITIES = {
  error: { label: "Error", color: "#c62828" },
  warning: { label: "Warning", color: "#b26a00" },
  info: { label: "Info", color: "#1565c0" },
};
const SEVERITY_ORDER = ["error", "warning", "info"];

const isGroupedWarning = (w) =>
  !!w &&
  typeof w.type === "string" &&
  typeof w.message === "string" &&
  !!SEVERITIES[w.severity] &&
  Array.isArray(w.affected);

// Returns the grouped warnings, or null when the payload is not in the grouped format
// (plain-text errors, Ecobalyse payloads, ...). An empty array is a valid "no warnings".
export const parseGroupedWarnings = (warnings) => {
  let value = warnings;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return Array.isArray(value) && value.every(isGroupedWarning) ? value : null;
};

// Number of root causes per severity.
export const warningCounts = (groups = []) =>
  groups.reduce(
    (counts, g) => ({ ...counts, [g.severity]: counts[g.severity] + 1 }),
    { error: 0, warning: 0, info: 0 },
  );

export const highestSeverity = (groups = []) =>
  SEVERITY_ORDER.find((s) => groups.some((g) => g.severity === s)) || null;

// "Contextos mezclados: (X: main=A, IS=B)" -> "(X: main=A, IS=B)"; the label has its own column.
export const warningDetail = (w) =>
  w.message.startsWith(`${w.label}: `) ? w.message.slice(w.label.length + 2) : "";

export const SeverityChip = ({ severity, count }) => {
  const { label, color } = SEVERITIES[severity];
  return (
    <Chip
      size="small"
      variant="outlined"
      label={count === undefined ? label : `${count} ${label.toLowerCase()}`}
      style={{ color, borderColor: color, marginRight: 4 }}
    />
  );
};

// List column: one chip per severity present, "—" when the log has no grouped warnings.
export const WarningCountChips = ({ warnings }) => {
  const groups = parseGroupedWarnings(warnings);
  if (!groups || groups.length === 0) return <span>—</span>;
  const counts = warningCounts(groups);
  return (
    <span aria-label="Warnings by severity">
      {SEVERITY_ORDER.filter((s) => counts[s] > 0).map((s) => (
        <SeverityChip key={s} severity={s} count={counts[s]} />
      ))}
    </span>
  );
};

// Datagrid column for the calculation log list.
export const WarningsField = ({ record: providedRecord }) => {
  const record = useRecordContext({ record: providedRecord }) || {};
  return <WarningCountChips warnings={record.warnings} />;
};

// Affected entities after this many are summarized; the JSON / copy keep the full list.
const MAX_AFFECTED_SHOWN = 100;

const AffectedList = ({ affected }) => {
  const shown = affected.slice(0, MAX_AFFECTED_SHOWN);
  return (
    <div>
      {shown.map((a) => (
        <Typography key={`${a.entity_type}-${a.id}`} variant="body2">
          {a.name || a.id}{" "}
          <Typography component="span" variant="caption" color="textSecondary">
            ({a.entity_type})
          </Typography>
        </Typography>
      ))}
      {affected.length > shown.length && (
        <Typography variant="caption" color="textSecondary">
          … and {affected.length - shown.length} more
        </Typography>
      )}
    </div>
  );
};

export const CalculationWarningsTable = ({ groups }) => {
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  return (
    <Table size="small" aria-label="Calculation warnings">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          <TableCell>Severity</TableCell>
          <TableCell>Warning</TableCell>
          <TableCell>Detail</TableCell>
          <TableCell>Stages</TableCell>
          <TableCell align="right">Affected</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.map((w) => {
          const key = `${w.type}|${w.message}`;
          const stages = w.stages || (w.stage ? [w.stage] : []);
          return (
            <Fragment key={key}>
              <TableRow hover onClick={() => toggle(key)} style={{ cursor: "pointer" }}>
                <TableCell padding="checkbox">
                  <IconButton size="small" aria-label={open[key] ? "Hide affected" : "Show affected"}>
                    {open[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <SeverityChip severity={w.severity} />
                </TableCell>
                <TableCell>{w.label}</TableCell>
                <TableCell>{warningDetail(w)}</TableCell>
                <TableCell>{stages.join(", ")}</TableCell>
                <TableCell align="right">{w.affected_count ?? w.affected.length}</TableCell>
              </TableRow>
              {open[key] && (
                <TableRow>
                  <TableCell />
                  <TableCell colSpan={5}>
                    <AffectedList affected={w.affected} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
